import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@haccp.au");
  const [password, setPassword] = useState("Admin123!");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Welcome back");
    navigate("/");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-bone">
      {/* Left - brand */}
      <div
        className="hidden md:block bg-eucalyptus-900 relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(rgba(27,51,41,0.88),rgba(27,51,41,0.94)),url('https://images.unsplash.com/photo-1658282687028-09f5b0b5b3ad?auto=format&fit=crop&w=1200&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-ochre-600 grid place-items-center font-display font-extrabold">
              H
            </div>
            <div>
              <div className="font-display font-bold text-xl">HACCP AU</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-eucalyptus-100">
                Food Safety Compliance
              </div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="label-eyebrow text-ochre-400">FSANZ 3.2.2 / 3.2.2A</div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold mt-3 tracking-tight leading-[1.05]">
              Audit-grade food safety.
              <span className="block text-ochre-400">From pass to Pilbara.</span>
            </h1>
            <p className="mt-6 text-eucalyptus-100 leading-relaxed">
              Temperature logs, receival checks, allergen control and thermal docket labels —
              all in one place, across every restaurant and remote camp kitchen.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-eucalyptus-100">
            <ShieldCheck className="w-4 h-4" />
            Built for Australian food businesses
          </div>
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={submit}
          className="w-full max-w-md surface p-8 md:p-10 animate-fade-in"
          data-testid="login-form"
        >
          <div className="label-eyebrow">Sign in</div>
          <h2 className="font-display text-3xl font-extrabold mt-2">Welcome back</h2>
          <p className="text-sm text-charcoal-soft mt-2">
            Use your work email to access your kitchen's HACCP records.
          </p>

          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
                required
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-ochre-600 bg-ochre-600/5 border border-ochre-600/20 rounded-md p-3"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white font-semibold"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          <div className="mt-8 p-4 surface-alt text-xs font-mono space-y-1 text-charcoal-soft">
            <div className="label-eyebrow mb-2">Demo accounts</div>
            <div>admin@haccp.au / Admin123!</div>
            <div>staff@haccp.au / Staff123!</div>
            <div>auditor@haccp.au / Auditor123!</div>
          </div>
        </form>
      </div>
    </div>
  );
}
